import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import Badge from "../../components/ui/Badge";
import Avatar from "../../components/ui/Avatar";
import Input from "../../components/forms/Input";
import { tickets, ticketComments } from "../../utils/mockData";

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [newComment, setNewComment] = useState("");

  const ticket = tickets.find((t) => t.id === parseInt(id));

  if (!ticket) {
    return (
      <PageWrapper title="Ticket Not Found">
        <div className="text-center py-12">
          <p className="text-light-500 mb-4">
            The ticket you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/tickets")}>Back to Tickets</Button>
        </div>
      </PageWrapper>
    );
  }

  const comments = ticketComments.filter((c) => c.ticketId === ticket.id);

  const getStatusVariant = (status) => {
    const variants = {
      open: "danger",
      "in-progress": "warning",
      resolved: "success",
      closed: "default",
    };
    return variants[status] || "default";
  };

  const getPriorityVariant = (priority) => {
    const variants = {
      critical: "danger",
      high: "warning",
      medium: "info",
      low: "success",
    };
    return variants[priority] || "default";
  };

  return (
    <PageWrapper
      title={`Ticket #${ticket.id}`}
      actions={
        <Button variant="ghost" onClick={() => navigate("/tickets")}>
          ← Back
        </Button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ticket Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card"
          >
            <h2 className="text-2xl font-bold text-light-100 mb-4">
              {ticket.title}
            </h2>
            <div className="flex flex-wrap gap-2">
              <Badge variant={getStatusVariant(ticket.status)}>
                {ticket.status}
              </Badge>
              <Badge variant={getPriorityVariant(ticket.priority)}>
                {ticket.priority}
              </Badge>
              <Badge>{ticket.category}</Badge>
            </div>
          </motion.div>

          {/* Comments Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card"
          >
            <h3 className="text-lg font-semibold text-light-100 mb-4">
              Comments
            </h3>
            <div className="space-y-4 mb-6">
              {comments.map((comment, idx) => (
                <motion.div
                  key={comment.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-3"
                >
                  <Avatar src="👤" alt={comment.author} size="md" />
                  <div className="flex-1 bg-dark-400 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-light-100">
                        {comment.author}
                      </span>
                      <span className="text-xs text-light-500">
                        {comment.timestamp}
                      </span>
                    </div>
                    <p className="text-light-200 text-sm">{comment.message}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Add Comment */}
            <div className="flex gap-3">
              <Avatar src="👤" alt="You" size="md" />
              <div className="flex-1 flex gap-2">
                <Input
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="flex-1"
                />
                <Button>Send</Button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Ticket Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="card"
          >
            <h3 className="text-lg font-semibold text-light-100 mb-4">
              Ticket Information
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-xs text-light-500 uppercase">
                  Assignee
                </span>
                <p className="text-light-100 font-medium">{ticket.assignee}</p>
              </div>
              <div>
                <span className="text-xs text-light-500 uppercase">
                  Created By
                </span>
                <p className="text-light-100 font-medium">{ticket.createdBy}</p>
              </div>
              <div>
                <span className="text-xs text-light-500 uppercase">
                  Created At
                </span>
                <p className="text-light-100 font-medium">{ticket.createdAt}</p>
              </div>
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="card"
          >
            <h3 className="text-lg font-semibold text-light-100 mb-4">
              Actions
            </h3>
            <div className="space-y-2">
              <Button variant="secondary" className="w-full">
                Change Status
              </Button>
              <Button variant="secondary" className="w-full">
                Reassign
              </Button>
              <Button variant="danger" className="w-full">
                Close Ticket
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default TicketDetails;
